import React, { Suspense, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { MuiTelInput } from "mui-tel-input";

import FilePreview from "../FilePreview";
import { DEFAULT_BOT_LAUNCHER_ICON } from "./mockData";
import { useBotEmbed } from "./BotEmbedContext";
import {
  getFixedEmbedPositionStyles,
  getFloatingWidgetPositionStyles,
  getViewportSize,
} from "./positioning";
import { LazyEmojiPicker } from "../my-chatboat/LiveBots/LazyEmojiPicker";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const MOBILE_MODAL_BREAKPOINT = 767;
const MOBILE_LANDSCAPE_HEIGHT_BREAKPOINT = 540;
const CODE_BLOCK_TOKEN_PREFIX = "__BB_MD_CODE_BLOCK__";
const EMBED_TEL_MENU_Z_INDEX = 2147483500;

const normalizeRichTextValue = (value = "") =>
  String(value || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");

const looksLikeHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizeMarkdownUrl = (value = "") => {
  const trimmedValue = String(value || "").trim();

  if (!trimmedValue) {
    return "";
  }

  if (/^(https?:|mailto:|tel:)/i.test(trimmedValue)) {
    return trimmedValue;
  }

  return "";
};

const formatInlineMarkdown = (value = "") => {
  const inlineCodeBlocks = [];
  let formattedValue = escapeHtml(value);

  formattedValue = formattedValue.replace(/`([^`]+)`/g, (_, codeText) => {
    const token = `__BB_MD_INLINE_CODE_${inlineCodeBlocks.length}__`;
    inlineCodeBlocks.push(`<code>${codeText}</code>`);
    return token;
  });

  formattedValue = formattedValue.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, label, rawUrl) => {
      const safeUrl = sanitizeMarkdownUrl(rawUrl);
      const safeLabel = label.trim() || safeUrl;

      if (!safeUrl) {
        return safeLabel;
      }

      return `<a href="${escapeHtml(safeUrl)}" rel="noreferrer" target="_blank">${safeLabel}</a>`;
    },
  );

  formattedValue = formattedValue
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>")
    .replace(/~~([^~]+)~~/g, "<del>$1</del>");

  inlineCodeBlocks.forEach((html, index) => {
    formattedValue = formattedValue.replace(
      `__BB_MD_INLINE_CODE_${index}__`,
      html,
    );
  });

  return formattedValue;
};

const renderMarkdownToHtml = (value = "") => {
  const normalizedValue = normalizeRichTextValue(value)
    .replace(/\r\n?/g, "\n")
    .trim();

  if (!normalizedValue) {
    return "";
  }

  const codeBlocks = [];
  const withCodePlaceholders = normalizedValue.replace(
    /```([\w-]+)?\n([\s\S]*?)```/g,
    (_, language = "", codeText = "") => {
      const token = `${CODE_BLOCK_TOKEN_PREFIX}${codeBlocks.length}__`;
      const languageLabel = language
        ? `<span class="bb-embed-code-language">${escapeHtml(language)}</span>`
        : "";

      codeBlocks.push(
        `<pre><code>${escapeHtml(codeText.replace(/\n$/, ""))}</code></pre>${languageLabel}`,
      );

      return token;
    },
  );

  const lines = withCodePlaceholders.split("\n");
  const htmlParts = [];
  let paragraphLines = [];
  let listType = null;
  let listItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return;
    }

    htmlParts.push(
      `<p>${paragraphLines.map((line) => formatInlineMarkdown(line)).join("<br />")}</p>`,
    );
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listType || !listItems.length) {
      return;
    }

    htmlParts.push(
      `<${listType}>${listItems
        .map((item) => `<li>${formatInlineMarkdown(item)}</li>`)
        .join("")}</${listType}>`,
    );
    listType = null;
    listItems = [];
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushParagraph();
      flushList();
      return;
    }

    if (trimmedLine.startsWith(CODE_BLOCK_TOKEN_PREFIX)) {
      flushParagraph();
      flushList();
      const codeBlockIndex = Number(
        trimmedLine.replace(CODE_BLOCK_TOKEN_PREFIX, "").replace("__", ""),
      );
      htmlParts.push(codeBlocks[codeBlockIndex] || "");
      return;
    }

    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const headingLevel = headingMatch[1].length;
      htmlParts.push(
        `<h${headingLevel}>${formatInlineMarkdown(headingMatch[2])}</h${headingLevel}>`,
      );
      return;
    }

    const quoteMatch = trimmedLine.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();
      htmlParts.push(
        `<blockquote><p>${formatInlineMarkdown(quoteMatch[1])}</p></blockquote>`,
      );
      return;
    }

    const unorderedListMatch = trimmedLine.match(/^[-*+]\s+(.*)$/);
    if (unorderedListMatch) {
      flushParagraph();
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listItems.push(unorderedListMatch[1]);
      return;
    }

    const orderedListMatch = trimmedLine.match(/^\d+\.\s+(.*)$/);
    if (orderedListMatch) {
      flushParagraph();
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      listItems.push(orderedListMatch[1]);
      return;
    }

    flushList();
    paragraphLines.push(trimmedLine);
  });

  flushParagraph();
  flushList();

  return htmlParts.join("");
};

const getRenderedMessageHtml = (message = {}) => {
  const normalizedText = normalizeRichTextValue(message.text || "");

  if (message.isHtml && looksLikeHtml(normalizedText)) {
    return normalizedText;
  }

  return renderMarkdownToHtml(normalizedText);
};

const isEventWithinNode = (event, node) => {
  if (!node) {
    return false;
  }

  if (typeof event.composedPath === "function") {
    return event.composedPath().includes(node);
  }

  return node.contains(event.target);
};

const getDownloadFileName = (href = "", fallbackLabel = "document") => {
  const rawFileName = href.split("/").pop()?.split("?")[0] || fallbackLabel;

  try {
    return decodeURIComponent(rawFileName);
  } catch (error) {
    return rawFileName;
  }
};

const hasInteractiveReplyOptions = (message = {}) => {
  const hasActionButtons = Array.isArray(message.buttons)
    ? message.buttons.some((button) => !button?.url)
    : false;

  return (
    hasActionButtons ||
    (Array.isArray(message.options) && message.options.length > 0)
  );
};

const isInteractiveMessageAnswered = (messages = [], messageIndex = -1) => {
  const message = messages[messageIndex];

  if (
    !message ||
    message.role !== "assistant" ||
    message.isAnswered ||
    !hasInteractiveReplyOptions(message)
  ) {
    return Boolean(message?.isAnswered);
  }

  const messageSessionId = message.sessionId || "";

  for (let index = messageIndex + 1; index < messages.length; index += 1) {
    const nextMessage = messages[index];

    if (!nextMessage) {
      continue;
    }

    const nextSessionId = nextMessage.sessionId || "";
    if (
      messageSessionId &&
      nextSessionId &&
      messageSessionId !== nextSessionId
    ) {
      continue;
    }

    if (nextMessage.role === "user") {
      return true;
    }
  }

  return false;
};

const getLatestAssistantMessage = (messages = []) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.role !== "assistant" || message?.isStreaming) {
      continue;
    }

    return message;
  }

  return null;
};

const getMessageTimestampValue = (message = {}) =>
  message?.serverDatetime ||
  message?.raw?.datetime ||
  message?.meta?.datetime ||
  "";

const getMessageTimestampValueAsLocal = (value = "") => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return null;
  }

  const sqlStyleTimestampMatch = normalizedValue.match(
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?$/,
  );

  if (sqlStyleTimestampMatch) {
    const [, timestampWithoutFraction, fractionalSecond = ""] =
      sqlStyleTimestampMatch;
    const normalizedSqlTimestamp = fractionalSecond
      ? `${timestampWithoutFraction}.${fractionalSecond.slice(0, 3).padEnd(3, "0")}`
      : timestampWithoutFraction;
    const sqlTimestampFormat = fractionalSecond
      ? "YYYY-MM-DD HH:mm:ss.SSS"
      : "YYYY-MM-DD HH:mm:ss";
    const parsedSqlTimestamp = dayjs.utc(
      normalizedSqlTimestamp,
      sqlTimestampFormat,
      true,
    );

    return parsedSqlTimestamp.isValid() ? parsedSqlTimestamp.local() : null;
  }

  const fallbackTimestamp = dayjs(normalizedValue);
  return fallbackTimestamp.isValid() ? fallbackTimestamp.local() : null;
};

const formatMessageTimestamp = (value = "") => {
  const dateValue = getMessageTimestampValueAsLocal(value);

  if (!dateValue) {
    return "";
  }

  return dateValue.format("DD MMM YYYY, hh:mm A");
};

const IconSend = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 20 20"
    width="16"
  >
    <path
      d="M17.5 2.5 9.854 17.292l-1.667-6.25L2.5 9.375 17.5 2.5Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const IconClose = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 20 20"
    width="16"
  >
    <path
      d="m5 5 10 10M15 5 5 15"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.6"
    />
  </svg>
);

const IconRefresh = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="14"
    viewBox="0 0 20 20"
    width="14"
  >
    <path
      d="M16.25 6.25V2.917m0 0H12.917m3.333 0-2.35 2.35A6.25 6.25 0 1 0 16.25 10"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const IconEmoji = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 20 20"
    width="18"
  >
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M7.25 12.1c.68.87 1.63 1.3 2.75 1.3s2.07-.43 2.75-1.3"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.5"
    />
    <circle cx="7.35" cy="8.1" fill="currentColor" r="0.95" />
    <circle cx="12.65" cy="8.1" fill="currentColor" r="0.95" />
  </svg>
);

const BotWidget = ({ muiPortalContainer = null }) => {
  const {
    attachmentUrl,
    composerError,
    config,
    currentInputType,
    draft,
    isOpen,
    isLoadingConfig,
    isTyping,
    isUploadingAttachment,
    isUsingFallback,
    isWaitingForFirstSocketMessage,
    loadError,
    messages,
    resetConversation,
    setDraft,
    setAttachmentUrl,
    setIsOpen,
    sendMessage,
  } = useBotEmbed();
  const [viewport, setViewport] = useState(() => getViewportSize());
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [activeLegalDocument, setActiveLegalDocument] = useState(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [multiChoiceSelection, setMultiChoiceSelection] = useState({
    messageId: "",
    options: [],
  });
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const transcriptRef = useRef(null);
  const activeAssistantMessage = getLatestAssistantMessage(messages);
  const activeMultiChoiceMessage =
    currentInputType === "multi-choice" &&
    Array.isArray(activeAssistantMessage?.options) &&
    activeAssistantMessage.options.length > 0
      ? activeAssistantMessage
      : null;
  const selectedMultiChoiceOptions =
    multiChoiceSelection.messageId &&
    multiChoiceSelection.messageId === activeMultiChoiceMessage?.id
      ? multiChoiceSelection.options
      : [];
  const hasSelectedMultiChoiceOptions = selectedMultiChoiceOptions.length > 0;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncViewport = () => {
      setViewport(getViewportSize());
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsDescriptionVisible(true);
      setIsEmojiPickerOpen(false);
      setActiveLegalDocument(null);
      setIsResetConfirmOpen(false);
      setMultiChoiceSelection({
        messageId: "",
        options: [],
      });
    }
  }, [config.setting_id, isOpen]);

  useEffect(() => {
    if (!activeMultiChoiceMessage) {
      setMultiChoiceSelection((currentSelection) =>
        currentSelection.messageId || currentSelection.options.length > 0
          ? {
              messageId: "",
              options: [],
            }
          : currentSelection,
      );
      return;
    }

    setMultiChoiceSelection((currentSelection) =>
      currentSelection.messageId === activeMultiChoiceMessage.id
        ? currentSelection
        : {
            messageId: activeMultiChoiceMessage.id,
            options: [],
          },
    );
  }, [activeMultiChoiceMessage]);

  useEffect(() => {
    const textareaElement = textareaRef.current;

    if (!textareaElement) {
      return;
    }

    textareaElement.style.height = "0px";
    textareaElement.style.height = `${textareaElement.scrollHeight}px`;
  }, [draft]);

  useEffect(() => {
    if (!isEmojiPickerOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!isEventWithinNode(event, emojiPickerRef.current)) {
        setIsEmojiPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEmojiPickerOpen]);

  useEffect(() => {
    if (!transcriptRef.current) {
      return;
    }

    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [isTyping, messages]);

  useEffect(() => {
    if (!activeLegalDocument && !isResetConfirmOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveLegalDocument(null);
        setIsResetConfirmOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeLegalDocument, isResetConfirmOpen]);

  const handleConversationReset = () => {
    setIsResetConfirmOpen(false);
    setIsEmojiPickerOpen(false);
    setActiveLegalDocument(null);
    resetConversation();
  };

  const fixedPositionStyles = getFixedEmbedPositionStyles(config, viewport);
  const fallbackPositionStyles = getFloatingWidgetPositionStyles(
    config,
    viewport,
  );
  const positionStyles = fixedPositionStyles || fallbackPositionStyles;
  const isFloatingWidget = !fixedPositionStyles;
  const isCompactLandscapeViewport =
    viewport.width > viewport.height &&
    viewport.height > 0 &&
    viewport.height <= MOBILE_LANDSCAPE_HEIGHT_BREAKPOINT;
  const isMobileModal =
    viewport.width > 0 &&
    (viewport.width <= MOBILE_MODAL_BREAKPOINT || isCompactLandscapeViewport);
  const isMobileLandscape = isMobileModal && viewport.width > viewport.height;
  const configuredDescriptionHeight = Number(config.desc_height);
  const shouldShowDescription =
    !isLoadingConfig &&
    !isOpen &&
    isDescriptionVisible &&
    Boolean(Number(config.isDescAllow)) &&
    Boolean(String(config.chat_bbl_desc || "").trim());
  const descriptionStyle = {
    ...(positionStyles?.description || {}),
    height:
      !isMobileModal &&
      Number.isFinite(configuredDescriptionHeight) &&
      configuredDescriptionHeight > 0
        ? `${configuredDescriptionHeight}px`
        : "fit-content",
  };
  const isStreamingMessageVisible = messages.some(
    (message) => message.isStreaming,
  );
  const isComposerBusy =
    isTyping || isWaitingForFirstSocketMessage || isUploadingAttachment;
  const isChoiceInput =
    currentInputType === "single-choice" || currentInputType === "multi-choice";
  const isSendDisabled =
    isComposerBusy ||
    (currentInputType === "multi-choice"
      ? !hasSelectedMultiChoiceOptions && !attachmentUrl
      : !draft.trim() && !attachmentUrl);
  const isTextInputDisabled = isComposerBusy || isChoiceInput;
  const toggleMultiChoiceOption = (message, option) => {
    if (!message?.id || !option) {
      return;
    }

    setMultiChoiceSelection((currentSelection) => {
      const nextOptions =
        currentSelection.messageId === message.id
          ? [...currentSelection.options]
          : [];
      const existingOptionIndex = nextOptions.findIndex(
        (selectedOption) =>
          selectedOption.id === option.id &&
          selectedOption.value === option.value &&
          selectedOption.label === option.label,
      );

      if (existingOptionIndex === -1) {
        nextOptions.push(option);
      } else {
        nextOptions.splice(existingOptionIndex, 1);
      }

      return {
        messageId: message.id,
        options: nextOptions,
      };
    });
  };
  const handleComposerSubmit = () => {
    if (currentInputType === "multi-choice") {
      if (!activeMultiChoiceMessage || !hasSelectedMultiChoiceOptions) {
        return;
      }

      const selectedOptionLabels = selectedMultiChoiceOptions
        .map((option) => option?.label || option?.value || "")
        .filter(Boolean);

      if (!selectedOptionLabels.length && !attachmentUrl) {
        return;
      }

      sendMessage(selectedOptionLabels.join(", "), {
        questionMeta: activeMultiChoiceMessage.meta,
      });
      setMultiChoiceSelection({
        messageId: "",
        options: [],
      });
      return;
    }

    sendMessage(draft);
  };
  const shouldShowLauncher = !(isMobileModal && isOpen && !isLoadingConfig);
  const isLauncherLoading = isLoadingConfig && !isUsingFallback;
  const launcherIconSrc =
    !isLauncherLoading && (config.slugs || DEFAULT_BOT_LAUNCHER_ICON);
  const handleLegalLinkClick = (event, { href, label, type }) => {
    if (!href) {
      event.preventDefault();
      return;
    }

    if (type === "url") {
      return;
    }

    if (type === "document") {
      event.preventDefault();

      const triggerDownload = (downloadHref) => {
        const link = document.createElement("a");
        link.href = downloadHref;
        link.download = getDownloadFileName(href, label);
        link.rel = "noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      fetch(href)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to download legal document");
          }

          return response.blob();
        })
        .then((blob) => {
          const objectUrl = window.URL.createObjectURL(blob);
          triggerDownload(objectUrl);
          window.setTimeout(() => {
            window.URL.revokeObjectURL(objectUrl);
          }, 0);
        })
        .catch(() => {
          triggerDownload(href);
        });

      return;
    }

    event.preventDefault();
    setActiveLegalDocument({
      href,
      label,
    });
  };

  return (
    <div
      className={`bb-embed-widget ${
        isFloatingWidget ? "bb-embed-widget--floating" : ""
      } ${isMobileModal ? "bb-embed-widget--mobile" : ""}`}
    >
      {shouldShowDescription ? (
        <div className="bb-embed-description" style={descriptionStyle}>
          <button
            aria-label="Hide description"
            className="bb-embed-description-close"
            onClick={() => setIsDescriptionVisible(false)}
            type="button"
          >
            X
          </button>
          <div className="bb-embed-description-body">
            <button
              aria-label={`Open ${config.bot_name}`}
              className="bb-embed-description-figure"
              onClick={() => setIsOpen(true)}
              type="button"
            >
              <img
                alt={config.bot_name}
                className="bb-embed-description-avatar"
                src={config.description_icon}
              />
            </button>
            <p>{config.chat_bbl_desc}</p>
          </div>
        </div>
      ) : null}

      <div
        className={`bb-embed-window ${
          isOpen && !isLoadingConfig ? "" : "bb-embed-window--closed"
        } ${isMobileModal ? "bb-embed-window--mobile-modal" : ""} ${
          isMobileLandscape
            ? "bb-embed-window--mobile-landscape"
            : isMobileModal
              ? "bb-embed-window--mobile-portrait"
              : ""
        }`}
        style={positionStyles?.window}
      >
        <div className="bb-embed-header">
          <div className="bb-embed-header-main">
            <img
              alt={config.bot_name}
              className="bb-embed-avatar"
              src={config.bot_image}
            />
            <div>
              <div className="bb-embed-title-row">
                <h2 className="bb-embed-title">{config.bot_name}</h2>
                <button
                  aria-label="Clear chat and start a new conversation"
                  className="bb-embed-clear-button"
                  onClick={() => setIsResetConfirmOpen(true)}
                  type="button"
                >
                  <IconRefresh />
                  Clear
                </button>
              </div>
              <p className="bb-embed-subtitle">
                {config.organizationName} | {config.tone}
              </p>
            </div>
          </div>
          <button
            aria-label="Close chatbot"
            className="bb-embed-icon-button"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <IconClose />
          </button>
        </div>

        <div className="bb-embed-body">
          {/* <div className="bb-embed-banner">
            <div className="bb-embed-banner-icon">
              <IconSpark />
            </div>
            <div>
              <strong>{config.template_name} preview</strong>
              <span>
                Open by default: {config.show_default ? "Yes" : "No"} | Model:{" "}
                {config.openRouterModel}
              </span>
            </div>
          </div> */}

          {isLoadingConfig ? (
            <div className="bb-embed-widget-note bb-embed-status bb-embed-status--loading">
              Loading live bot appearance settings...
            </div>
          ) : isUsingFallback ? (
            <div className="bb-embed-widget-note bb-embed-status bb-embed-status--fallback">
              {loadError || "Using fallback appearance settings."}
            </div>
          ) : null}

          <div
            className="bb-embed-transcript bb-embed-scrollbar"
            ref={transcriptRef}
          >
            {isWaitingForFirstSocketMessage ? (
              <div className="bb-embed-widget-note bb-embed-status bb-embed-status--loading">
                Connecting to chat...
              </div>
            ) : (
              <>
                {messages.map((message, messageIndex) => {
                  const isMessageAnswered = isInteractiveMessageAnswered(
                    messages,
                    messageIndex,
                  );
                  const messageTimestamp = formatMessageTimestamp(
                    getMessageTimestampValue(message),
                  );

                  return (
                    <div
                      className={`bb-embed-message bb-embed-message--${message.role}`}
                      key={message.id}
                    >
                      <div
                        className={`bb-embed-bubble bb-embed-bubble--${
                          message.role === "assistant" ? "assistant" : "user"
                        }`}
                      >
                        {message.attachmentUrl ? (
                          <div className="bb-embed-attachment">
                            <FilePreview fileLink={message.attachmentUrl} />
                          </div>
                        ) : null}

                        {message.text ? (
                          <div
                            className="bb-embed-bubble-html"
                            dangerouslySetInnerHTML={{
                              __html: getRenderedMessageHtml(message),
                            }}
                          />
                        ) : null}

                        {message.role === "assistant" &&
                        Array.isArray(message.buttons) &&
                        message.buttons.length > 0 ? (
                          <div className="bb-embed-message-actions">
                            {message.buttons.map((button) =>
                              button.url ? (
                                <a
                                  className="bb-embed-message-link"
                                  href={button.url}
                                  key={button.id}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  {button.label}
                                </a>
                              ) : (
                                <button
                                  className="bb-embed-message-action"
                                  disabled={isComposerBusy || isMessageAnswered}
                                  key={button.id}
                                  onClick={() =>
                                    sendMessage(button.label, {
                                      option: button,
                                      inputType: "single-choice",
                                      questionMeta: message.meta,
                                    })
                                  }
                                  type="button"
                                >
                                  {button.label}
                                </button>
                              ),
                            )}
                          </div>
                        ) : null}

                        {message.role === "assistant" &&
                        Array.isArray(message.options) &&
                        message.options.length > 0 ? (
                          <div className="bb-embed-message-actions">
                            {message.options.map((option) => (
                              <button
                                className={`bb-embed-message-action ${
                                  message.type === "multi-choice" &&
                                  selectedMultiChoiceOptions.some(
                                    (selectedOption) =>
                                      selectedOption.id === option.id &&
                                      selectedOption.value === option.value &&
                                      selectedOption.label === option.label,
                                  ) &&
                                  activeMultiChoiceMessage?.id === message.id
                                    ? "bb-embed-message-action--selected"
                                    : ""
                                }`}
                                disabled={isComposerBusy || isMessageAnswered}
                                key={option.id}
                                onClick={() =>
                                  message.type === "multi-choice"
                                    ? toggleMultiChoiceOption(message, option)
                                    : sendMessage(option.label, {
                                        option,
                                        inputType:
                                          message.type || "single-choice",
                                        questionMeta: message.meta,
                                      })
                                }
                                type="button"
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        ) : null}

                        {message.role === "assistant" &&
                        Array.isArray(message.links) &&
                        message.links.length > 0 ? (
                          <div className="bb-embed-message-links">
                            {message.links.map((link) => (
                              <a
                                className="bb-embed-message-link"
                                href={link.url}
                                key={link.id}
                                rel="noreferrer"
                                target="_blank"
                              >
                                {link.label}
                              </a>
                            ))}
                          </div>
                        ) : null}

                        {messageTimestamp ? (
                          <div
                            className={`bb-embed-message-time bb-embed-message-time--${message.role}`}
                          >
                            {messageTimestamp}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}

                {isTyping && !isStreamingMessageVisible && (
                  <div className="bb-embed-message bb-embed-message--assistant">
                    <div className="bb-embed-bubble bb-embed-bubble--assistant">
                      <div
                        className="bb-embed-typing"
                        aria-label="Bot is typing"
                      >
                        <span className="bb-embed-dot" />
                        <span className="bb-embed-dot" />
                        <span className="bb-embed-dot" />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bb-embed-quick-actions">
            {config.quickActions.map((action) => (
              <button
                className="bb-embed-quick-action"
                disabled={isComposerBusy}
                key={action.id}
                onClick={() => sendMessage(action.label)}
                type="button"
              >
                {action.label}
              </button>
            ))}
          </div>

          <form
            className="bb-embed-input"
            onSubmit={(event) => {
              event.preventDefault();
              handleComposerSubmit();
            }}
          >
            {attachmentUrl ? (
              <div className="bb-embed-composer-attachment">
                <FilePreview
                  closeButton={true}
                  fileLink={attachmentUrl}
                  setFileURL={setAttachmentUrl}
                />
              </div>
            ) : null}

            {composerError ? (
              <div className="bb-embed-inline-note bb-embed-status bb-embed-status--fallback">
                {composerError}
              </div>
            ) : null}

            <div className="bb-embed-input-row">
              {/* <button
                aria-label="Attach file"
                className="bb-embed-emoji-trigger"
                disabled={isComposerBusy}
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <IconAttach />
              </button> */}
              {/* <input
                className="bb-embed-file-input"
                onChange={(event) =>
                  uploadAttachment(event.target.files?.[0] || null)
                }
                onClick={(event) => {
                  event.target.value = null;
                }}
                ref={fileInputRef}
                type="file"
              /> */}

              <div className="bb-embed-emoji-wrap" ref={emojiPickerRef}>
                <button
                  aria-expanded={isEmojiPickerOpen}
                  aria-label="Open emoji picker"
                  className="bb-embed-emoji-trigger"
                  disabled={isComposerBusy}
                  onClick={() => setIsEmojiPickerOpen((current) => !current)}
                  type="button"
                >
                  <IconEmoji />
                </button>

                {isEmojiPickerOpen ? (
                  <div className="bb-embed-emoji-picker">
                    <Suspense
                      fallback={<div className="bb-embed-emoji-loading" />}
                    >
                      <LazyEmojiPicker
                        onSelect={(emoji) => {
                          setDraft(
                            (currentDraft) => `${currentDraft}${emoji.native}`,
                          );
                          setIsEmojiPickerOpen(false);
                          textareaRef.current?.focus();
                        }}
                      />
                    </Suspense>
                  </div>
                ) : null}
              </div>

              {currentInputType === "phone" ? (
                <MuiTelInput
                  autoComplete="off"
                  className="bb-embed-tel-input"
                  defaultCountry="IN"
                  disableAreaCodes={true}
                  disabled={isComposerBusy}
                  enableLongNumbers={true}
                  forceCallingCode
                  fullWidth={true}
                  id="bb-embed-phone-input"
                  MenuProps={
                    muiPortalContainer
                      ? {
                          container: muiPortalContainer,
                          slotProps: {
                            root: {
                              sx: {
                                zIndex: EMBED_TEL_MENU_Z_INDEX,
                              },
                            },
                          },
                        }
                      : undefined
                  }
                  onChange={(value) => setDraft(value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      sendMessage(draft);
                    }
                  }}
                  value={draft}
                  variant="outlined"
                />
              ) : (
                <textarea
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={isTextInputDisabled}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleComposerSubmit();
                    }
                  }}
                  placeholder={
                    currentInputType === "multi-choice"
                      ? "Select one or more options above"
                      : isChoiceInput
                        ? "Choose an option above"
                        : "Type a message"
                  }
                  ref={textareaRef}
                  rows={1}
                  value={draft}
                />
              )}
              <button
                className="bb-embed-send"
                disabled={isSendDisabled}
                type="submit"
              >
                <IconSend />
                Send
              </button>
            </div>
          </form>
        </div>

        <div className="bb-embed-footer">
          <div className="bb-embed-pills">
            {/* <span className="bb-embed-pill">
              {config.message_per_session} msg/session
            </span>
            {config.human_takeover_attempt ? (
              <span className="bb-embed-pill">Human takeover</span>
            ) : null} */}
          </div>
          <div className="bb-embed-pills">
            {config.ppLink_value ? (
              <a
                download={config.ppLink_type === "url" ? undefined : true}
                href={config.ppLink_value}
                onClick={(event) =>
                  handleLegalLinkClick(event, {
                    href: config.ppLink_value,
                    label: "Privacy Policy",
                    type: config.ppLink_type,
                  })
                }
                rel={config.ppLink_type === "url" ? "noreferrer" : undefined}
                target={config.ppLink_type === "url" ? "_blank" : undefined}
              >
                Privacy
              </a>
            ) : null}
            {config.tmsLink_value ? (
              <a
                download={config.tmsLink_type === "url" ? undefined : true}
                href={config.tmsLink_value}
                onClick={(event) =>
                  handleLegalLinkClick(event, {
                    href: config.tmsLink_value,
                    label: "Terms of Service",
                    type: config.tmsLink_type,
                  })
                }
                rel={config.tmsLink_type === "url" ? "noreferrer" : undefined}
                target={config.tmsLink_type === "url" ? "_blank" : undefined}
              >
                Terms
              </a>
            ) : null}
          </div>
        </div>
      </div>

      {shouldShowLauncher ? (
        <button
          aria-expanded={isOpen}
          aria-label={isOpen ? "Hide assistant" : "Open assistant"}
          className="bb-embed-launcher bb-embed-launcher--compact"
          onClick={() => setIsOpen((currentState) => !currentState)}
          style={{ ...positionStyles?.launcher, zIndex: 2147482999 }}
          type="button"
        >
          {launcherIconSrc ? (
            <img alt={config.bot_name} src={launcherIconSrc} />
          ) : (
            <span
              aria-hidden="true"
              className="bb-embed-launcher-placeholder"
            />
          )}
        </button>
      ) : null}

      {activeLegalDocument ? (
        <div
          className="bb-embed-legal-modal"
          onClick={() => setActiveLegalDocument(null)}
          role="presentation"
        >
          <div
            className="bb-embed-legal-modal-card"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={activeLegalDocument.label}
          >
            <div className="bb-embed-legal-modal-header">
              <strong>{activeLegalDocument.label}</strong>
              <button
                aria-label="Close document"
                className="bb-embed-legal-modal-close"
                onClick={() => setActiveLegalDocument(null)}
                type="button"
              >
                <IconClose />
              </button>
            </div>
            <iframe
              className="bb-embed-legal-modal-frame"
              src={activeLegalDocument.href}
              title={activeLegalDocument.label}
            />
          </div>
        </div>
      ) : null}

      {isResetConfirmOpen ? (
        <div
          className="bb-embed-legal-modal"
          onClick={() => setIsResetConfirmOpen(false)}
          role="presentation"
        >
          <div
            aria-label="Clear chat confirmation"
            aria-modal="true"
            className="bb-embed-confirm-modal-card"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
          >
            <div className="bb-embed-legal-modal-header">
              <strong>Start a new chat?</strong>
              <button
                aria-label="Close confirmation"
                className="bb-embed-legal-modal-close"
                onClick={() => setIsResetConfirmOpen(false)}
                type="button"
              >
                <IconClose />
              </button>
            </div>
            <div className="bb-embed-confirm-modal-body">
              This will clear the current conversation and reconnect the chat as
              a new browser session.
            </div>
            <div className="bb-embed-confirm-modal-actions">
              <button
                className="bb-embed-confirm-button bb-embed-confirm-button--secondary"
                onClick={() => setIsResetConfirmOpen(false)}
                type="button"
              >
                No
              </button>
              <button
                className="bb-embed-confirm-button bb-embed-confirm-button--danger"
                onClick={handleConversationReset}
                type="button"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BotWidget;
